import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { ProgressBarIndicator } from 'secondstep-components'
import AdminGradeList from 'components/AdminGradeList'
import AdminLessonProgressCard from 'components/AdminLessonProgressCard'
import AdminBHULessonProgressCard from 'components/AdminBHULessonProgressCard'
import AdminNoClasses from 'components/AdminNoClasses'
import PlaybookSteps from 'components/admin/PlaybookSteps'
import { CLASSES_NOT_STARTED_MESSAGE, NOT_STARTED_MESSAGE } from './constants'
import {
  BodyWrapper,
  ClassesProgressWrapper,
  ClassProgressBullet,
  ClassesNotStartedText,
  ClassesNotStartedWrapper,
  ClassesStartedText,
  GoalTextClasses,
  LessonCompletionWrapper,
  NoClassesMessage,
  PanelWrapper,
  PipeSeparator,
  SiteCollapsible,
  StyledAdminLessonProgressTabs,
  StyledHairline,
  StyledHeader,
  CardHeading,
} from './component.styles'
import { isFlagOn } from 'utils/featureFlags'

const LessonCompletionAccordion = ({
  activeIndex,
  classesCreated,
  classesNotStarted,
  grades,
  handleAccordionClick,
  index,
  lastUpdatedAt,
  name,
  siteId,
  sitePreferences,
  summary,
  units,
}) => {
  const decimalToPercentage = decimal => {
    return decimal > 0
      ? decimal < 0.01
        ? Math.ceil(decimal * 100)
        : Math.round(decimal * 100)
      : decimal
  }
  const { completionPercentage } = summary ?? 0
  const lessonCompletionPercentage = decimalToPercentage(completionPercentage)
  const hasBHUEnabled =
    sitePreferences?.mspDigital?.bullyingHarassmentUnitEnabled ?? false
  const hasBHUClassCreated = summary.unit5.classesCreated > 0
  const bhuStatus = hasBHUEnabled && hasBHUClassCreated
  const monitorGoals = isFlagOn([
    'feature_LEARN-23862_monitorgoals_guided_playbook',
  ])

  return (
    <SiteCollapsible
      accordionPanelDataTestId={`school-row`}
      ariaLabel={`${name}-accordion`}
      classesNotStarted={classesNotStarted}
      dataTestId={`site-collapsible-${siteId}`}
      followActiveProp={true}
      isActive={activeIndex === index}
      key={siteId}
      onClick={() => handleAccordionClick(index)}
      title={
        <PanelWrapper>
          <StyledHeader classesNotStarted={classesNotStarted}>
            {name}
          </StyledHeader>
          <ClassesProgressWrapper classesNotStarted={classesNotStarted}>
            {classesCreated === 0 && (
              <NoClassesMessage>{CLASSES_NOT_STARTED_MESSAGE}</NoClassesMessage>
            )}
            {classesCreated > 0 && (
              <Fragment key="classes-created-key">
                <Fragment key="classes-not-started-key">
                  <Fragment key="classes-created-goal-key">
                    <ClassesStartedText>{classesCreated}</ClassesStartedText>

                    <PipeSeparator>|</PipeSeparator>
                    <GoalTextClasses>
                      {`Goal: 
                      ${
                        sitePreferences?.k5Digital.classCreationGoalValue
                          ? sitePreferences?.k5Digital.classCreationGoalValue
                          : 'Not Set'
                      }`}
                    </GoalTextClasses>
                  </Fragment>
                  <ClassesNotStartedWrapper>
                    <ClassProgressBullet />
                    <ClassesNotStartedText>
                      {classesNotStarted} {NOT_STARTED_MESSAGE}
                    </ClassesNotStartedText>
                  </ClassesNotStartedWrapper>
                </Fragment>
              </Fragment>
            )}
          </ClassesProgressWrapper>
          <LessonCompletionWrapper classesCreated={classesCreated}>
            <ProgressBarIndicator
              percentComplete={lessonCompletionPercentage}
              shouldRenderIndicator
            />
            <PipeSeparator>|</PipeSeparator>
            <GoalTextClasses>{`Goal: 
              ${
                sitePreferences?.k5Digital.lessonCompletionGoalValue
                  ? `${sitePreferences?.k5Digital.lessonCompletionGoalValue}%`
                  : 'Not Set'
              }`}</GoalTextClasses>
          </LessonCompletionWrapper>
        </PanelWrapper>
      }
    >
      {classesCreated > 0 && !monitorGoals && (
        <StyledAdminLessonProgressTabs bhuStatus={bhuStatus} />
      )}
      <PlaybookSteps
        key={`monitor-${siteId}`}
        showMonitorViewOnly={true}
        siteId={siteId}
        sitePreferences={sitePreferences}
      />
      <BodyWrapper classesCreated={classesCreated}>
        {classesCreated === 0 && <AdminNoClasses />}
        {classesCreated > 0 && (
          <Fragment key="body-wrapper">
            {monitorGoals && (
              <CardHeading>Core Curriculum (Units 1-4)</CardHeading>
            )}
            {classesCreated > 0 && monitorGoals && (
              <StyledAdminLessonProgressTabs bhuStatus={bhuStatus} />
            )}
            {!monitorGoals && (
              <AdminLessonProgressCard
                bhuStatus={bhuStatus}
                lastUpdatedAt={lastUpdatedAt}
                siteId={siteId}
                summary={summary}
                units={units}
              />
            )}
            <StyledHairline />
            <AdminGradeList grades={grades} siteId={siteId} />
          </Fragment>
        )}
      </BodyWrapper>
      {bhuStatus && (
        <AdminBHULessonProgressCard
          classesCreated={classesCreated}
          grades={grades}
          siteId={siteId}
          summary={summary}
        />
      )}
    </SiteCollapsible>
  )
}

LessonCompletionAccordion.propTypes = {
  activeIndex: PropTypes.number,
  classesCreated: PropTypes.number,
  classesNotStarted: PropTypes.number,
  grades: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  handleAccordionClick: PropTypes.func,
  index: PropTypes.number,
  lastUpdatedAt: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string,
  siteId: PropTypes.number,
  sitePreferences: PropTypes.object,
  summary: PropTypes.object,
  units: PropTypes.array,
}

export default LessonCompletionAccordion
